import { RotateCcw } from 'lucide-react';
import React, { useLayoutEffect, useRef } from 'react';
import sendChatBtnIcon from '../assets/send_chat_btn.png';
import type { CatalogDocument } from '../hooks/useDocumentMention';
import DocumentChips from './DocumentChips';
import './MessageInput.css';

interface MessageInputProps {
  input: string;
  isLoading: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  placeholder?: string;
  selectedDocs?: CatalogDocument[];
  onDocRemove?: (docId: string) => void;
  mentionDropdown?: React.ReactNode;
}

const MessageInput: React.FC<MessageInputProps> = ({
  input,
  isLoading,
  onInputChange,
  onSubmit,
  onKeyPress,
  placeholder = "Ask anything",
  selectedDocs,
  onDocRemove,
  mentionDropdown,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Size the box to its content on every value change, not just on typing:
  // prefills, restored/recovered drafts, and mention insertions all set `input`
  // programmatically, and would otherwise stay at the one-row initial height
  // with the rest of the text scrolled out of sight. Empty input falls back to
  // the CSS min-height, so a send resets the box.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    if (input !== '') {
      el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
    }
  }, [input]);

  return (
      <form onSubmit={onSubmit} className="relative" style={{width:'100%', display:'flex', margin:'0 auto', padding:'16px'}}>
        {/* Input Container - Responsive */}
        <div className="message-input-container-flex relative w-full max-w-[832px] mx-auto min-h-[76px] bg-white border border-[rgba(231,229,228,0.8)] shadow-[0px_1px_7px_rgba(0,0,0,0.1)] rounded-2xl">

          {mentionDropdown}

          {selectedDocs && onDocRemove && (
            <DocumentChips docs={selectedDocs} onRemove={onDocRemove} />
          )}

          {/* Textarea and send button share one row so the button sits on the
              text's own line instead of floating below it. */}
          <div className="message-input-row">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={onInputChange}
              onKeyDown={onKeyPress}
              rows={1}
              placeholder={placeholder}
              className="message-input-textarea"
              disabled={isLoading}
            />

            {/* Send Button */}
            <button
              type="submit"
              disabled={!input.trim() || isLoading}
              className="rounded-lg flex items-center justify-center transition-opacity message-input-send-button-inline"
              title={isLoading ? "Sending..." : "Send message"}
            >
              {isLoading ? (
                <div className="w-9 h-9 sm:w-[36px] sm:h-[36px] bg-[#D1D5DB] rounded-lg flex items-center justify-center">
                  <RotateCcw className="w-3 h-3 sm:w-[12px] sm:h-[16px] text-white animate-spin" />
                </div>
              ) : (
                <img
                  src={sendChatBtnIcon}
                  alt="Send message"
                  className="w-9 h-9 sm:w-[36px] sm:h-[36px] hover:opacity-80 transition-opacity"
                />
              )}
            </button>
          </div>

        </div>
      </form>
  );
};

export default MessageInput;
